import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, ShoppingBag, Users, ScanLine, MapPin, BarChart3, Mail, Megaphone, Settings as Cog, LogOut, Menu, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, LOGO_URL } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["OWNER", "EVENT_MANAGER", "DOOR_STAFF"] },
  { to: "/admin/events", label: "Events", icon: CalendarDays, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/customers", label: "Customers", icon: Users, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/checkin", label: "Check-In", icon: ScanLine, roles: ["OWNER", "EVENT_MANAGER", "DOOR_STAFF"] },
  { to: "/admin/venues", label: "Venues", icon: MapPin, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/contacts", label: "Contacts", icon: Mail, roles: ["OWNER", "EVENT_MANAGER"] },
  { to: "/admin/settings", label: "Settings", icon: Cog, roles: ["OWNER"] },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5 border-b border-[#27272A]">
        <img src={LOGO_URL} alt="ABE" className="h-8 logo-invert" />
        <p className="text-[10px] text-zinc-500 mt-2 font-oswald tracking-widest">{user?.role}</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF37] text-black" : "text-zinc-300 hover:bg-[#18181B]"}`}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <button data-testid="change-password-btn" onClick={() => { setOpen(false); setPwOpen(true); }} className="mx-3 mt-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-zinc-400 hover:bg-[#18181B] hover:text-white">
        <KeyRound size={18} /> Change Password
      </button>
      <button data-testid="admin-logout" onClick={doLogout} className="m-3 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-zinc-400 hover:bg-[#18181B] hover:text-white">
        <LogOut size={18} /> Sign Out
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0D0D10] flex">
      <aside className="hidden md:flex w-60 flex-col border-r border-[#27272A] bg-[#0D0D10] fixed inset-y-0">
        <SidebarContent />
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col bg-[#0D0D10] border-r border-[#27272A]"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-60 min-w-0">
        <header className="md:hidden sticky top-0 z-40 flex items-center justify-between border-b border-[#27272A] bg-[#0D0D10] px-4 h-14">
          <img src={LOGO_URL} alt="ABE" className="h-6 logo-invert" />
          <button data-testid="admin-menu-toggle" onClick={() => setOpen((o) => !o)} className="text-white">{open ? <X /> : <Menu />}</button>
        </header>
        <div className="p-4 sm:p-6 max-w-6xl">
          <Outlet />
        </div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-[#121215] border-[#27272A] text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="cp-current" type="password" placeholder="Current password" className="w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white text-sm outline-none focus:border-[#D4AF37]" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            <input data-testid="cp-next" type="password" placeholder="New password" className="w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white text-sm outline-none focus:border-[#D4AF37]" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            <input data-testid="cp-confirm" type="password" placeholder="Confirm new password" className="w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white text-sm outline-none focus:border-[#D4AF37]" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            <button data-testid="cp-submit" disabled={saving} onClick={changePassword} className="btn-gold w-full rounded-md py-3 font-oswald tracking-widest disabled:opacity-60">{saving ? "UPDATING…" : "UPDATE PASSWORD"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
